/**
 * Service comparison tables — surfaced on ServiceDetail pages to help
 * visitors pick the right structure or option, and to give AI answer
 * engines something extractable to cite.
 *
 * `serviceComparisons[<service-id>]` returns a table shape:
 *   {
 *     title, intro,
 *     headers: [string, ...],
 *     rows: [[string, ...], ...],
 *     footnote (optional),
 *   }
 */

export const serviceComparisons = {
  'company-registration': {
    title: 'Pty Ltd vs NPC vs Cooperative — which company type fits?',
    intro:
      'South Africa recognises several company structures. Each has different owner-liability, tax and reporting rules. Use this table as a quick shortlist, then confirm with a consultant before filing at CIPC.',
    headers: ['Feature', '(Pty) Ltd', 'NPC', 'Co-operative', 'Sole Proprietor'],
    rows: [
      ['Legal person', 'Yes', 'Yes', 'Yes', 'No — owner and business are one'],
      ['Minimum incorporators', '1 director + 1 shareholder', '3 directors', '5 members', '1 (the owner)'],
      ['Owner liability', 'Limited to share value', 'None (non-profit)', 'Limited (usually R100 each)', 'Unlimited personal liability'],
      ['Income-tax rate', '27% corporate', 'Exempt if PBO status granted', '27% or SBC rates', 'Personal marginal rates'],
      ['CIPC annual return', 'Yes — R100 to R3 000', 'Yes — R100', 'Yes — R100', 'None (no CIPC record)'],
      ['Best for', 'Trading businesses, tenders, funding', 'Charities, NGOs, non-profits', 'Farming, worker collectives', 'Freelancers testing an idea'],
      ['Consultium price (from)', 'R590', 'R990', 'R1 250', 'Not applicable'],
    ],
    footnote:
      'Prices are subject to CIPC fees and complexity. SBC = Small Business Corporation. PBO = Public Benefit Organisation.',
  },
  'beneficial-ownership': {
    title: 'Beneficial ownership disclosure — who has to file?',
    intro:
      'Since 24 May 2023, CIPC requires almost every registered entity to keep and file a beneficial ownership register. This table shows what applies to which entity type in 2026.',
    headers: ['Entity type', 'Must file at CIPC?', 'Threshold (share/voting/control)', 'Update within'],
    rows: [
      ['Private (Pty) Ltd', 'Yes', '5% or more, or control', '10 business days'],
      ['Public company', 'Yes (extended disclosure)', '5% or more, or control', '10 business days'],
      ['Personal liability (Inc.)', 'Yes', '5% or more, or control', '10 business days'],
      ['NPC (Non-profit)', 'Yes', 'Any founding member / controller', '10 business days'],
      ['Close corporation (CC)', 'Yes', 'Any member holding 5% or more', '10 business days'],
      ['Co-operative', 'Yes', 'Any member with control', '10 business days'],
      ['Trust', 'No (files with the Master, not CIPC)', 'All natural persons behind trust', 'As per Master rules'],
    ],
    footnote:
      'A "beneficial owner" is a natural person who ultimately owns or controls the entity, whether directly or indirectly through holding companies, nominees or arrangements.',
  },
  'annual-returns': {
    title: '2026 CIPC annual return fee schedule',
    intro:
      'CIPC annual return fees are turnover-based. Filing is compulsory even for dormant companies. Late filings attract penalties and, ultimately, deregistration.',
    headers: ['Annual turnover', 'Fee (filed on time)', 'Late-filing risk'],
    rows: [
      ['Under R1 million', 'R100', 'Penalties + possible deregistration'],
      ['R1 million – R10 million', 'R450', 'Penalties + possible deregistration'],
      ['R10 million – R25 million', 'R2 000', 'Penalties + possible deregistration'],
      ['Over R25 million', 'R3 000', 'Penalties + possible deregistration'],
    ],
    footnote:
      'CIPC recovers roughly 20 000 companies from deregistration every year. Reinstatement costs meaningfully more than filing the annual return on time.',
  },
};

export const getComparisonForService = (serviceId) => serviceComparisons[serviceId] || null;
